import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Type-to-search raw material picker, sorted A-Z. The list floats above everything
// (position: fixed) so it is never cut off inside scrolling tables.
export default function MaterialPicker({ materials, value, onChange, placeholder = 'Select raw material', invalid = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const sorted = useMemo(
    () => [...(materials || [])].sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })),
    [materials]
  );
  const selected = sorted.find((m) => String(m.id) === String(value));

  const results = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return sorted;
    return sorted.filter((m) => {
      const hay = `${m.name} ${m.category || ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [sorted, query]);

  const place = () => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 280);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    const below = window.innerHeight - r.bottom;
    if (below < 260 && r.top > below) setPos({ left, width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(320, r.top - 12) });
    else setPos({ left, width, top: r.bottom + 4, maxHeight: Math.min(320, below - 12) });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target) && listRef.current && !listRef.current.contains(e.target)) setOpen(false);
    };
    const onMove = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-i="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (m) => {
    onChange(String(m.id));
    setOpen(false);
    setQuery('');
  };

  const openPicker = () => {
    setQuery('');
    setOpen(true);
    const i = selected ? sorted.findIndex((m) => m.id === selected.id) : 0;
    setActive(Math.max(0, i));
  };

  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { e.preventDefault(); openPicker(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[active]) choose(results[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Tab') setOpen(false);
  };

  const border = invalid ? '2px solid #e53935' : open ? '1.5px solid #5b3fd9' : '1px solid #e2dcea';

  return (
    <div ref={boxRef} style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : selected ? `${selected.name} (${selected.unit})` : ''}
        placeholder={open ? 'Type to search…' : placeholder}
        onFocus={openPicker}
        onClick={() => { if (!open) openPicker(); }}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onKeyDown={onKey}
        autoComplete="off"
        style={{
          width: '100%', boxSizing: 'border-box', height: 44, padding: '0 36px 0 12px',
          border, borderRadius: 10, background: invalid ? '#fff5f5' : '#fff',
          font: 'inherit', fontSize: 14, fontWeight: selected && !open ? 600 : 500, color: '#231a33', outline: 'none', cursor: 'text',
        }}
      />
      <span aria-hidden="true" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#776e86', fontSize: 12 }}>
        {open ? '▲' : '▼'}
      </span>

      {open && pos && (
        <div
          ref={listRef}
          role="listbox"
          style={{
            position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight,
            overflowY: 'auto', zIndex: 2000, background: '#fff', border: '1px solid #e2dcea', borderRadius: 12,
            boxShadow: '0 16px 40px -12px rgba(35,26,51,0.35)', padding: 4,
          }}
        >
          {results.length === 0 && <div style={{ padding: '12px 10px', color: '#776e86', fontSize: 13 }}>No raw material matches “{query}”</div>}
          {results.map((m, i) => (
            <div
              key={m.id}
              data-i={i}
              role="option"
              aria-selected={String(m.id) === String(value)}
              onMouseDown={(e) => { e.preventDefault(); choose(m); }}
              onMouseEnter={() => setActive(i)}
              style={{
                display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
                background: i === active ? '#efeaff' : 'transparent',
                fontWeight: String(m.id) === String(value) ? 800 : 500, fontSize: 14, color: '#231a33',
              }}
            >
              <span>{m.name}</span>
              <span style={{ color: '#776e86', fontSize: 12, whiteSpace: 'nowrap' }}>{m.unit}{m.category ? ` · ${m.category}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
